<template>
  <div class="performance-indicators">
    <!-- Header Section -->
    <section class="pi-hero">
      <div class="hero-content">
        <h1 class="hero-title">Performance Indicators of Transport</h1>
      </div>
    </section>

    <!-- Main Content Container -->
    <div class="main-container">
      <!-- Left Tree Structure -->
      <div :class="['tree-panel', { collapsed: isTreeCollapsed }]">
        <div class="tree-header">
          <h3>Transport Modes</h3>
          <button @click="toggleTree" class="collapse-btn">
            <i
              :class="
                isTreeCollapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-left'
              "
            ></i>
          </button>
        </div>

        <div class="tree-content" v-show="!isTreeCollapsed">
          <div class="tree-structure">
            <div
              v-for="mode in transportModes"
              :key="mode.id"
              class="tree-node"
            >
              <!-- Mode Level -->
              <div
                @click="toggleMode(mode.id)"
                :class="[
                  'tree-item mode-item',
                  {
                    active: selectedMode === mode.id,
                    expanded: expandedModes.includes(mode.id),
                  },
                ]"
              >
                <i
                  :class="[
                    'expand-icon',
                    expandedModes.includes(mode.id)
                      ? 'fas fa-chevron-down'
                      : 'fas fa-chevron-right',
                  ]"
                ></i>
                <i :class="['mode-icon', mode.icon]"></i>
                <span class="item-label">{{ mode.name }}</span>
              </div>

              <!-- Categories Level -->
              <div
                v-show="expandedModes.includes(mode.id)"
                class="tree-children"
              >
                <div
                  v-for="category in mode.categories"
                  :key="category.id"
                  @click="selectCategory(mode.id, category.id)"
                  :class="[
                    'tree-item category-item',
                    {
                      active:
                        selectedMode === mode.id &&
                        selectedCategory === category.id,
                    },
                  ]"
                >
                  <i :class="['category-icon', category.icon]"></i>
                  <span class="item-label">{{ category.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Content Panel -->
      <div class="content-panel">
        <!-- Breadcrumb Navigation -->
        <div class="breadcrumb">
          <span v-if="selectedMode">{{ getCurrentMode().name }}</span>
          <span v-if="selectedCategory">
            > {{ getCurrentCategory().name }}</span
          >
        </div>

        <!-- Content Area -->
        <div class="content-area">
          <!-- Mode Overview -->
          <div v-if="selectedMode && !selectedCategory" class="mode-overview">
            <div v-if="getCurrentMode().type === 'indicator'">
              <div class="mode-header">
              <h2>{{ getCurrentMode().name }}</h2>
              </div>
              <p class="mode-description">{{ getCurrentMode().description }}</p>
              <div class="detail-section">
              <h4>Definition</h4>
              <p>{{ getCurrentMode().definition }}</p>
              </div>
              <div v-for="(item, idx) in getCurrentMode().indicators" :key="idx" class="detail-section">
              <h4>{{ item.title }}</h4>
              <ul>
                <li v-for="(bullet, bidx) in item.bullets" :key="bidx">{{ bullet }}</li>
              </ul>
              </div>
            </div>
            <div v-else>
              <div class="mode-header">
              <i :class="['mode-icon-large', getCurrentMode().icon]"></i>
              <h2>{{ getCurrentMode().name }}</h2>
              </div>
              <p class="mode-description">{{ getCurrentMode().description }}</p>
              <div class="categories-grid">
              <div
                v-for="category in getCurrentMode().categories"
                :key="category.id"
                @click="selectCategory(selectedMode, category.id)"
                class="category-card"
              >
                <i :class="['category-icon-large', category.icon]"></i>
                <h3>{{ category.name }}</h3>
                <p>{{ category.description }}</p>
                <span class="indicator-count"
                >{{ category.indicators.length }} indicators</span
                >
              </div>
              </div>
            </div>

          </div>

          <!-- Category Content -->
          <div v-if="selectedCategory" class="category-content">
            <div class="category-header">
              <i
                :class="['category-icon-large', getCurrentCategory().icon]"
              ></i>
              <h2>{{ getCurrentCategory().name }}</h2>
            </div>
            <p class="category-description">
              {{ getCurrentCategory().description }}
            </p>

            <div class="indicators-list">
              <div
                v-for="indicator in getCurrentCategory().indicators"
                :key="indicator.id"
                class="indicator-card "
              >
                <div class="indicator-header">
                  <i class="fas fa-chart-line"></i>
                  <h3>{{ indicator.name }}</h3>
                </div>
                <p>{{ indicator.description }}</p>

                <div v-if="indicator.definition" class="indicator-details">
                  <div class="detail-section">
                    <h4>Definition</h4>
                    <p>{{ indicator.definition }}</p>
                  </div>

                  <div v-if="indicator.challenges" class="detail-section">
                    <h4>Challenges & Best Practices</h4>
                    <p v-if="indicator.challenges.description">
                      {{ indicator.challenges.description }}
                    </p>
                    <ul
                      v-if="
                        indicator.challenges.bullets &&
                          indicator.challenges.bullets.length > 0
                      "
                    >
                      <li
                        v-for="bullet in indicator.challenges.bullets"
                        :key="bullet"
                      >
                        {{ bullet }}
                      </li>
                    </ul>
                  </div>

                  <div v-if="indicator.dataRequirements" class="detail-section">
                    <h4>Data Requirements</h4>
                    <ul>
                      <li v-for="req in indicator.dataRequirements" :key="req">
                        {{ req }}
                      </li>
                    </ul>
                  </div>

                  <div
                    v-if="indicator.enhancementOpportunities"
                    class="detail-section"
                  >
                    <h4>Enhancement Opportunities</h4>
                    <ul>
                      <li
                        v-for="enhancement in indicator.enhancementOpportunities"
                        :key="enhancement"
                      >
                        {{ enhancement }}
                      </li>
                    </ul>
                  </div>

                  <div v-if="indicator.useCase" class="detail-section">
                    <h4>Use Case</h4>
                    <p>{{ indicator.useCase }}</p>

                    <div v-if="indicator.image">
                      <!-- <h4>Visualization</h4> -->
                      <div v-if="Array.isArray(indicator.image)">
                        <div
                          v-for="(img, idx) in indicator.image"
                          :key="idx"
                          class="image-container"
                        >
                          <img :src="img.src" :alt="img.alt" />
                          <p class="image-caption">{{ img.caption }}</p>
                        </div>
                      </div>
                      <div v-else class="image-container">
                        <img
                          :src="indicator.image.src"
                          :alt="indicator.image.alt"
                        />
                        <p class="image-caption">
                          {{ indicator.image.caption }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Default State -->
          <div v-if="!selectedMode" class="default-content">
            <div class="welcome-message">
              <i class="fas fa-map-marked-alt"></i>
              <h2>Welcome to Performance Indicators of Transport</h2>
              <p>
                Select a transport mode from the left panel to explore
                comprehensive performance metrics and indicators.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PerformanceIndicators",
  data() {
    return {
      isTreeCollapsed: false,
      selectedMode: null,
      selectedCategory: null,
      expandedModes: [],
      transportModes: [
        {
          id: "motor",
          name: "Motor Vehicles",
          icon: "fas fa-car",
          description:
            "Performance indicators for road-based motor vehicle transportation including cars, trucks, and motorcycles.",
          categories: [
            {
              id: "acc",
              name: "Access and Connectivity",
              icon: "fas fa-road",
              description:
                "Metrics related to population access to transportation infrastructure and connectivity to essential services.",
              indicators: [
                {
                  id: "1",
                  name: "Rural Access Indicator (RAI)",
                  description:
                    "Share of rural population within 2km of all-season roads",
                  definition:
                    "The share of the rural population living within 2 kilometers of an all-season road in good or fair condition. This definition is based on the assumption that rural residents are willing to walk 20-25 kilometers to access all-season roads.",
                  challenges: {
                    description:
                      "Significant differences in data availability and quality within and across developing countries. Using a robust and flexible RAI estimation methodology that accommodates varying data inputs, temporal and spatial resolutions, and data update frequencies.",
                  },
                  dataRequirements: [
                    "Estimate of rural populations",
                    "Road network and road surface type/conditions",
                    "Available modes for accessing an all-season road",
                  ],
                  enhancementOpportunities: [
                    "Socio-demographic/census data",
                    "Historical weather conditions",
                    "Terrain information",
                  ],
                  useCase:
                    "The 2019 study by Workman and McPherson at the Research for Community Access Partnership offered a modernized approach to estimate RAI. The study applied their methodology to develop a 2006 World Bank study, 2016 World Bank study, and a global RAI measurement tool.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_Acc_1.jpg"),
                    alt: "RAI Estimates Chart",
                    caption: "RAI Estimates by Country and Methodology",
                  },
                },
                {
                  id: "2",
                  name: "Access to Opportunities and Essential Services",
                  description:
                    "Population access to key services and opportunities",
                  definition:
                    "The percentage of opportunities that can be reached within 30 minutes. The percentage of the population that has access based on distance, however, is also commonly used.",
                  challenges: {
                    description:
                      "Requires knowledge of the road network and measures of mobility on the road network, such as travel times by time of day. Break down accessibility by day-of-week and time of day. Consider seasonality in areas with significant weather variation.",
                  },
                  dataRequirements: [
                    "Road network map",
                    "Points of interest",
                    "Travel times",
                  ],
                  enhancements: [
                    "Time of day travel times",
                    "Day of week and seasonal travel times",
                  ],
                  useCase:
                    "The 2022 study by van Heerden et al. analyzed multi-modal access to opportunities and essential services in the city of Tshwane, South Africa.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_acc_2.jpg"),
                    alt: "Accessibility Chart for Tshwane",
                    caption:
                      "Motor Vehicle Accessibility for Three Regions in Tshwane, South Africa",
                  },
                },
              ],
            },
            {
              id: "mob",
              name: "Mobility of People",
              icon: "fas fa-users",
              description:
                "Indicators measuring the movement and flow of people using motor vehicle transportation.",
              indicators: [
                {
                  id: "1",
                  name:
                    "Motor Vehicle Volumes and Demand (Direct Observations)",
                  description: "Traffic flow parameters and demand modeling",
                  definition:
                    "The use of traffic simulation and modeling techniques to assess traffic flow parameters, such as volume and speeds, when field volume counts are not feasible.",
                  challenges: {
                    description: "",
                    bullets: [
                      "High resource requirements to install and maintain ATRs. ",
                      "Limited number of ATRs due to resource constraints.",
                      "Conduct studies to model and simulate vehicle demand as an alternative to extensive ATR deployment, particularly challenging in developing countries",
                    ],
                  },
                  dataRequirements: [
                    "Road network map",
                    "Basic ATR output data",
                  ],
                  enhancementOpportunities: [
                    "Road Classifications",
                    "Advanced ATR output data",
                  ],
                  useCase:
                    "Studies such as Salari et al. (2019) developed a genetic algorithm-based method to locate a minimum set of ATRs to estimate network-level volumes that include roads with and without ATR stations. The study applied the methodology to identify the location of ATRs in Sioux Falls, South Dakota, USA. The study found that having more advanced sensors on major roads and minimizing the number of major road links without ATRs improves the estimation of volumes for the network.",
                },
                {
                  id: "3",
                  name:
                    "Motor Vehicle Volumes and Demand (Based on Modeling/Simulation)",
                  description:
                    "Traffic simulation and modeling to estimate vehicle volumes and demand.",
                  definition:
                    "Recognizing that field volume counts are not always feasible, traffic simulation and modeling techniques are often used to assess traffic flow parameters such as volume and speeds.",
                  challenges: {
                    bullets: [
                      "High resource requirements to install and maintain ATRs.",
                      "Limited number of ATRs due to resource constraints.",
                      "Conduct studies to model and simulate vehicle demand as an alternative to extensive ATR deployment, particularly in developing countries.",
                    ],
                  },
                  dataRequirements: [
                    "Road network map",
                    "Initial estimates of volumes/demand by time of day",
                  ],
                  enhancementOpportunities: [
                    "Vehicle class volumes",
                    "Lane specific volumes",
                  ],
                  useCase:
                    "The 2015 study by Arkatkar et al. used commercial traffic simulation software, VISSIM, to simulate traffic flow on the Delhi-Gurgaon Expressway in India. The study evaluated the speed-flow density diagrams of the expressway to compare simulated traffic flow with field observations. The VISSIM simulation (blue bars) was able to accurately reflect the hourly traffic flows based on field observations (red bars). 5-minute aggregation intervals were used for comparison.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_mob_1.jpg"),
                    alt: "Traffic Flow Simulation Chart",
                    caption:
                      "Comparing Simulated Traffic Flows with Field Observed Traffic Flows",
                  },
                },
                {
                  id: "4",
                  name:
                    "Motor Vehicle Speeds (Space Mean Speeds, Spot Speeds, and Instantaneous Speeds)",
                  description:
                    "Speed data for mobility, congestion, and driver behavior analysis",
                  definition:
                    "i) Space mean speeds are average speeds over a distance, ii) spot speeds are speeds at a specific point, and iii) instantaneous speeds are immediate speeds at any given moment, often used to analyze driver behavior.",
                  challenges: {
                    bullets: [
                      "Choosing the appropriate speed data type is crucial due to their different characteristics.",
                      "Space mean speeds lack spatial variation insight, while spot speeds require assumptions for unsensed segments.",
                      "Assess the analysis needs before data collection to select the suitable speed data type.",
                      "Utilize space mean speeds for overall traffic stream mobility and spot speeds for spatial speed variation understanding.",
                    ],
                  },
                  dataRequirements: ["Space mean speeds", "Spot speeds"],
                  enhancementOpportunities: [
                    "Speeds segregated by vehicle class",
                    "Lane specific speeds",
                    "Road surface conditions",
                    "Location of regulatory traffic control devices",
                  ],
                  useCase:
                    'The study by Shilling and Waetjen (2020) assessed the impact of Covid-19 on traffic system performance in California, USA. The analysis used spot speed detectors at target locations across the state to count traffic volumes and speeds before and after Covid-19 "shelter-in-place" orders.',
                  image: {
                    src: require("@/assets/images/PIT/Motor_mob_2.jpg"),
                    alt: "Speed Distribution Chart",
                    caption:
                      "Comparing Simulated Traffic Flows with Field Observed Traffic Flows",
                  },
                },
                {
                  id: "ttnd",
                  name: "Travel Times and Delay",
                  description:
                    "Travel time and delay analysis for road segments and corridors.",
                  definition:
                    "Travel times and delay provide insights on how efficiently motor vehicles can travel between origins and destinations. In addition, travel time data is a valuable performance indicator for individual road segments.",
                  challenges: {
                    bullets: [
                      "Reliance on household transportation surveys for travel behavior data.",
                      "Difficulty in incorporating real-world travel times into delay studies.",
                      "Traditional methods unsuitable for network-wide estimation.",
                      "Use technological solutions like LPR cameras, Bluetooth sensors, and vehicle trajectory data for accurate travel time measurement.",
                      "Utilize vehicle probe data for aggregated travel times on predefined road segments, updating regularly for precise delay estimation.",
                    ],
                  },
                  dataRequirements: [
                    "Road Network Map",
                    "Corridor Specific Travel Times",
                  ],
                  enhancementOpportunities: [
                    "Posted Speed Limit",
                    "Incident and Event Data",
                    "Location of Regulatory Traffic Control Devices",
                    "Value of Time for Passenger and Commercial Vehicle Road Users",
                    "Road Surface Conditions",
                    "Vehicle Class Volumes",
                    "Vehicle Occupancy Rates",
                  ],
                  useCase:
                    "The research by Gimenez-Nadal et al. (2021) analyzed European commuting trends from the European Working Conditions Survey from 1995, 2000, 2015, 2010, and 2015. The study compared reported commuting travel times in 15 European countries. The study found that most countries report increases in travel times over the analysis period. However, Greece, Portugal, and Germany showed a general reduction in travel times over the analysis period. The study also found that several countries reported that men tend to devote more time to their commute than women.",
                },
                {
                  id: "ci",
                  name: "Congestion Index",
                  description: "Traffic congestion measurement and monitoring",
                  definition:
                    "Congestion index provides a congestion score, often based on the speed, density, and flow rate of the traffic stream. In general, a higher score represents lower or no congestion, while a lower score represents more severe congestion.",
                  challenges: {
                    description: "",
                    bullets: [
                      "Requires significant resources for data collection, primarily from Automated Traffic Recorders (ATRs).",
                      "Investing in technologies for widespread data collection.",
                    ],
                  },
                  dataRequirements: [
                    "Traffic speeds, density, and flow rates - Speed, density, and flow rates are needed to establish and assign various levels of the congestion index. Each of these data items can be collected using ATRs, however other methods such as reduction of video feeds may also be used.",
                  ],
                  enhancementOpportunities: [
                    "Posted speed limit",
                    "Incident and event data",
                    "Location of regulatory traffic control devices",
                    "Road surface conditions",
                  ],
                  useCase:
                    "The 2007 study by Vaziri et al. developed congestion indices in Iran using limited sample data. The study analyzed two freeways and two arterials in the city of Tehran. To do so, the researchers developed a congestion index based on desired travel speed, observed travel speed, and travel rate (minutes per kilometer). The calculation of the congestion index used data extracted from video feeds on the target road segments to compare the indices from linear and non-linear models to HCM based methods.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_mob_3.jpg"),
                    alt: "Congestion Index Chart",
                    caption:
                      "Congestion Index by Time of Day for Selected Road Segments in Tehran, Iran",
                  },
                },
                {
                  id: "ttri",
                  name: "Travel Time Reliability Index (TTRI)",
                  description:
                    "Measures the consistency of travel times under varying congestion conditions.",
                  definition:
                    "Congestion index provides a congestion score, often based on the speed, density, and flow rate of the traffic stream. In general, a higher score represents lower or no congestion, while a lower score represents more severe congestion.",
                  challenges: {
                    description:
                      "The computation of TTRs requires the regular collection of travel times for the target segments and routes. To overcome this challenge, many agencies rely on third-party vehicle probe data which provides time measurements at regular intervals such as 1 or 5 minutes.",
                  },
                  dataRequirements: [
                    "Segment and/or route specific travel times – Travel times under both congested and uncongested conditions are required to estimate each TTR performance indicator.",
                  ],
                  enhancementOpportunities: [
                    "Posted speed limit",
                    "Weather conditions",
                    "Incident and event data",
                    "Location of regulatory traffic control devices",
                  ],
                  useCase:
                    "The 2024 report by the Maryland Department of Transportation – State Highway Administration (MDOT-SHA) used several TTR performance indicators to quantify the mobility of Maryland’s road network. The report assessed trends in VMT and TTR performance indicators that are segregated by road class (e.g. freeway and arterial). The study found that while congestion and TTR performance indicators were generally improved during the COVID-19 pandemic, many roads had returned to pre-pandemic conditions in terms of congestion severity and travel time reliability.",
                },
                {
                  id: "iri",
                  name: "International Roughness Index (IRI)",
                  icon: "fas fa-ruler-horizontal",
                  definition:
                    "The IRI, developed by the World Bank in the 1980s, measures road roughness by simulating a quarter-car's suspension motion over a specified distance at 80 km/hr, offering insights for construction and pavement maintenance.",
                  description:
                    "Measures road roughness and pavement condition for maintenance and construction insights.",
                  challenges: {
                    bullets: [
                      "Selecting appropriate IRI estimation models is complex due to model complexity and data requirements.",
                      "Validating IRI demands significant resources for field measurements.",
                      "Analysts may develop or adopt models from previous studies based on available input data to overcome these challenges.",
                    ],
                  },
                  dataRequirements: [
                    "Pavement Age",
                    "Thickness of Overlay",
                    "Estimate of Loads",
                    "Temperature",
                    "Precipitation",
                  ],
                  enhancementOpportunities: [
                    "Percent of Wheel Path with Fatigue Cracking",
                    "Rut Depth",
                    "Soil Stability",
                  ],
                  useCase:
                    "The work by Abdelaziz et al. (2020) developed several IRI models for flexible pavements including linear, exponential, and an artificial neural network (ANN). As shown below, the proposed model had a correlation coefficient of 0.57 with field measured IRI.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_mob_4.jpg"),
                    alt: "IRI Model Performance Chart",
                    caption:
                      "Performance of the Proposed IRI Model Compared to Field Measured IRI",
                  },
                },
                {
                  id: "bridgeinventoryconditions",
                  name: "Bridge Inventory and Conditions",
                  icon: "fas fa-archway",
                  description:
                    "Assessment of bridge condition and inventory status.",
                  definition:
                    "While there are several methods to evaluate bridge conditions such as the Bridge Condition Index (BCI) and the National Bridge Inventory (NBI), most use a tiered rating score (e.g., good, fair, or poor).",
                  challenges: {
                    bullets: [
                      "Visual inspections are cost-effective but lack continuous monitoring and rely on subjective assessment.",
                      "Installed monitoring systems offer continuous monitoring but are costly to install, maintain, and complex.",
                      "Combine visual inspection with monitoring systems to balance cost, data accuracy, and system complexity.",
                    ],
                  },
                  dataRequirements: [
                    "Conditions of the following: Bridge deck, Expansion joints, Foundation, Girders and beams, Trusses, cables, and braces.",
                  ],
                  enhancementOpportunities: [
                    "Estimates of vibration",
                    "Surface and structure strain",
                    "Displacement",
                    "Scour detection",
                    "Voltage potential of steel components",
                    "Slope",
                    "Temperature and wind speed/direction",
                  ],
                  useCase:
                    "The work by Agdas et al. (2016) compared the advantages and disadvantages of visual inspection and installed monitoring systems. The study investigated the cost of both methods. The table below presents the estimated cost of visual inspections.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_mob_5.jpg"),
                    alt: "Bridge Inspection Cost Table",
                    caption: "Cost of Visual Inspection Components",
                  },
                },
                {
                  id: "asset",
                  name: "Roadway Asset Management and Maintenance",
                  icon: "fas fa-tools",
                  description:
                    "Assessment and management of roadway assets and safety hardware.",
                  definition:
                    "Ownership and maintenance of roadways inherently includes the management of all assets in the right-of-way. Ensuring that traffic control devices and safety hardware are in reasonably good condition is critical to providing safe and efficient transportation services.",
                  challenges: {
                    bullets: [
                      "Monitoring the condition of all roadway assets can be a challenge as it requires significant resources in data collection and documentation.",
                      "Using modern technologies such as LIDAR and cameras that can be mounted to a data collection vehicle, which automate the data collection process by collecting data as the vehicle drives along the road.",
                    ],
                  },
                  dataRequirements: [
                    "Location and device conditions of the following items should be measured on a regular basis: Pavement markings, traffic signs, traffic signals, and safety hardware.",
                  ],
                  enhancementOpportunities: [
                    "Continuous pavement marking measurement",
                    "Enhanced assessment of traffic control device retroreflectivity",
                    "Improved ability to assess sight distance",
                  ],
                  useCase:
                    "A 2014 study by Davis and Donath developed a sensor-based system for roadway asset management using GPS sensors and LIDAR. The system was tested in both urban and rural driving environments. The study found that in both environments, the centerline measurement was accurate within 6 cm while the detection of guardrails was accurate within 10 cm.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_mob_5.jpg"),
                    alt: "Roadway Asset Management System Image",
                    caption:
                      "Example of a Roadway Asset Management System Using LIDAR and GPS",
                  },
                },
              ],
            },
            {
              id: "saf",
              name: "Safety",
              icon: "fas fa-shield-alt",
              description:
                "Safety performance metrics and crash analysis for motor vehicle transportation.",
              indicators: [
                {
                  id: "1",
                  name: "Crash Counts and Crash Rates",
                  description: "Traffic accident statistics and safety metrics",
                  definition:
                    "Crash counts are normalized by vehicle miles traveled (VMT) on each segment. Crash rates are used to compare safety performance across locations.",
                  challenges: {
                    bullets: [
                      "Collecting crash data requires significant resources from incident management, enforcement, and emergency response teams.",
                      "Electronic data entry forms can improve efficiency.",
                    ],
                  },
                  dataRequirements: [
                    "Date, time, and location of crashes",
                    "VMT estimates for exposure rate comparison",
                  ],
                  enhancementOpportunities: [
                    "Severity of injuries",
                    "Vehicle types involved",
                    "Victim details",
                    "Driving environment details",
                    "Emergency response details",
                  ],
                  useCase:
                    "Jadaan et al. (2018) compared crash rates in developed and developing countries, finding decreases in developed countries and mixed results elsewhere.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_saf_1.jpg"),
                    alt: "Crash Rate Trends Chart",
                    caption: "Crash Rate Trends for Selected Countries",
                  },
                },
                {
                  id: "2",
                  name: "Speed Variance (Surrogate Measure of Safety)",
                  description: "Speed variance as a safety indicator",
                  definition:
                    "Speed variance provides insight into traffic flow turbulence, enabling proactive safety interventions.",
                  challenges: {
                    bullets: [
                      "Requires speed sensors or ATRs at target locations.",
                      "Vehicle probe data can provide speeds for segments with sufficient observations.",
                    ],
                  },
                  dataRequirements: [
                    "Date, time, and location of speed observations",
                  ],
                  enhancementOpportunities: [
                    "Lane-specific speeds",
                    "Individual vehicle speeds",
                  ],
                  useCase:
                    "Kloeden et al. (2001) found that higher speed variance increases crash risk on rural Australian roads.",
                },
                {
                  id: "3",
                  name: "Vehicle Event Records",
                  description:
                    "Records of events such as harsh braking or acceleration, often from telematics or smartphone traces.",
                  definition:
                    "Event data includes harsh braking, acceleration, turn-signal use, seat belt usage, and headlights, typically from high-resolution vehicle traces.",
                  challenges: {
                    bullets: [
                      "Requires access to high-resolution vehicle trajectory data.",
                      "Third-party vendors provide anonymized event data with location and time stamps.",
                    ],
                  },
                  dataRequirements: [
                    "Date, time, and location of vehicle event observations",
                  ],
                  enhancementOpportunities: [
                    "Technology penetration rates for representativeness",
                  ],
                  useCase:
                    "Kandiboina et al. (2023) found connected vehicle data accurately detected most medium and heavy severity events compared to ATR stations.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_saf_2.jpg"),
                    alt: "Vehicle Event Data Sample Image",
                    caption: "Event Detection Summary",
                  },
                },
                {
                  id: "4",
                  name:
                    "Safety Performance Functions and Crash Modification Factors",
                  description:
                    "Statistical models for crash prediction and safety countermeasure evaluation",
                  definition:
                    "Safety Performance Functions (SPF) predict crash numbers on road segments. Crash Modification Factors (CMF) estimate the expected crash reduction after implementing safety measures.",
                  challenges: {
                    bullets: [
                      "Collecting CMF and SPF data requires significant resources and advanced statistical evaluation.",
                      "Leverage existing literature for initial estimates of SPFs and CMFs.",
                    ],
                  },
                  dataRequirements: [
                    "Crash history of target segments",
                    "Estimates of CMFs",
                    "Inputs for SPF",
                  ],
                  enhancementOpportunities: ["N/A"],
                  useCase:
                    "Srinivasan and Bauer (2013) provided guidance on developing and applying agency-specific SPFs and CMFs, with detailed examples for implementation.",
                },
              ],
            },

            {
              id: "sus",
              name: "Sustainability",
              icon: "fas fa-leaf",
              description:
                "Environmental impact and sustainability metrics for motor vehicle transportation.",
              indicators: [
                {
                  id: "1",
                  name: "Energy Consumption and Emissions",
                  description: "Environmental impact assessment",
                  definition:
                    "Energy consumption is the amount of energy used by vehicles, typically measured as fuel consumption for combustion engines or electricity use for electric vehicles. Energy emissions are the pollutants released by vehicles.",
                  challenges: {
                    description:
                      "Field measurements of fuel consumption and emissions take significant resources to acquire sensors for direct measurement.",
                    bullets: [
                      "Adopt modeling methods from other studies, accounting for engine types (gas, diesel, hybrid, electric), vehicle age/condition, and road surface type.",
                    ],
                  },
                  dataRequirements: [
                    "Traffic volumes",
                    "Estimate of percentage of each engine type",
                    "Vehicle speeds",
                  ],
                  enhancementOpportunities: [
                    "Vehicle registration data",
                    "Vehicle trajectory data",
                    "Local fuel consumption by type",
                  ],
                  useCase:
                    "Lee (2018) assessed policy impacts on long-term vehicular emissions. Type I Profile assumes slower growth in emissions; Type II Profile assumes initial increase then decrease as technologies are adopted.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_sus_1.jpg"),
                    alt: "Vehicular Emissions Projection Chart",
                    caption:
                      "Impact of Various Policies on Emissions Versus ‘Do Nothing’ Scenario",
                  },
                },
                {
                  id: "2",
                  name: "EV Adoption Rate and Charging Infrastructure",
                  description: "Electric vehicle adoption and infrastructure",
                  definition:
                    "As EVs become more ubiquitous, agencies must adapt to ensure the infrastructure can handle the shift to EVs. To do so, agencies must monitor EV adoption rates, availability of EV charging stations, and the impact on the local energy grid.",
                  challenges: {
                    bullets: [
                      "Significant resources required to collect and analyze EV adoption and charging station installation data.",
                      "Maintaining vehicle registration databases and policies for charging infrastructure development.",
                    ],
                  },
                  dataRequirements: [
                    "Vehicle registration data – allows agencies to monitor the transition to EVs and EV adoption rates.",
                    "Existing EV charging infrastructure – location and usage of EV charging stations to assess infrastructure support for EV adoption.",
                  ],
                  enhancementOpportunities: [
                    "Capacity of energy grid – estimating the impact of EV charging on the grid to ensure electricity services are not disrupted.",
                  ],
                  useCase:
                    "The synopsis by Rajper and Albrecht (2020) summarized the prospects and challenges of EV adoption in developing countries. The research found that reduced emissions, low operational costs, and energy savings attracted motorists to using EVs. However, factors such as high purchase price, lack of government policies, and range anxiety discouraged EV adoption.",
                },
              ],
            },
            {
              id: "fre",
              name: "Freight and Trade",
              icon: "fas fa-truck",
              description:
                "Freight movement and commercial vehicle performance indicators.",
              indicators: [
                {
                  id: "1",
                  name:
                    "Commercial Motor Vehicle (CMV) Travel Times and Reliability",
                  description: "Commercial vehicle performance metrics",
                  definition:
                    "CMVs are a critical component in the movement of freight and impact the economy. Monitoring CMV mobility is required to ensure that goods reach their destinations within a reasonable timeframe.",
                  challenges: {
                    bullets: [
                      "Tracking the movement of CMVs is challenging because freight transport companies often restrict the details about how goods are moved.",
                      "Partnering with third-party CMV trajectory and probe data providers to access specific CMV data and enforcing CMV laws through the use of scale houses, virtual weigh stations, license plate reading cameras, or CCTV networks to track and monitor freight vehicles effectively.",
                    ],
                  },
                  dataRequirements: [
                    "CMV volumes",
                    "CMV speeds and travel times",
                  ],
                  enhancementOpportunities: [
                    "CMV trajectory data",
                    "Commodity flow data",
                  ],
                  useCase:
                    "In their 2024 study, Franz et al. used CMV trajectory data in Maryland to evaluate detouring behavior due to enforcement, traffic congestion, and a major crash that closed northbound I-95. The study showed that during the closure, CMV trips on I-95 dropped from 62% to 36%, while detour route US-50 saw an increase from 28% to 44%.",
                  image: {
                    src: require("@/assets/images/PIT/Motor_fre_1.jpg"),
                    alt: "CMV Route Usage Map",
                    caption: "CMV route usage on normal day and incident day",
                  },
                },
                {
                  id: "2",
                  name: "CMV Safety",
                  description: "Commercial vehicle safety statistics",
                  definition:
                    "CMVs are subject to unplanned safety inspections which ensure that the driver and vehicle are meeting the minimum safety standards.",
                  challenges: {
                    bullets: [
                      "CMV inspection and citation data requires dedicated enforcement of CMV laws.",
                      "Use of virtual weigh stations (VWS) that measure axle loads, speeds, and vehicle heights at speed. VWS data can be communicated to an enforcement officer downstream of the VWS to provide recommendations on which vehicles may be violating CMV laws.",
                    ],
                  },
                  dataRequirements: [
                    "CMV inspection data",
                    "CMV citation data",
                  ],
                  enhancementOpportunities: ["N/A"],
                  useCase:
                    "The work by Forlines et al. (2019) estimated the benefits of automated CMV enforcement in Kentucky, USA. Automated enforcement can not only increase safety of officers, but also increase citation rates and generate additional revenue for further safety improvements.",
                },
              ],
            },
          ],
        },

        {
          id: "transit",
          name: "Transit",
          icon: "fas fa-bus",
          description: "",
          categories: [
            {
              id: "acc",
              name: "Access and Connectivity",
              icon: "fas fa-road",
              description: "",
              indicators: [
                {
                  id: "urban-accessibility-index",
                  name: "Urban Accessibility Index (UAI)",
                  description: "",
                  definition:
                    "UAI measures the distance or time to access opportunities or critical services using public transportation and walking. The World Bank's version defines it as the percentage of jobs accessible within a 60-minute commute by walking and public transportation.",
                  challenges: {
                    description:
                      "Estimating UAI requires fusion of multiple data sources:",
                    bullets: [
                      "Population estimates/Location and operating hours/Transit options available to access opportunities and critical services/Schedule",
                      "Select the most appropriate version of UAI based on data availability.",
                    ],
                  },
                  dataRequirements: [
                    "Estimate of urban populations",
                    "Points of Interest",
                    "Travel times and distances",
                  ],
                  enhancementOpportunities: [
                    "Socio-demographic/census data",
                    "Observed transit travel times",
                    "Service alerts and trip modifications",
                  ],
                  useCase:
                    "The research by Järv et al. (2018) developed a dynamic urban accessibility model that considered the temporal variations in population, transport services, and hours of operation for points of interest. The proposed method was applied to a real-world use case in Tallinn, Estonia. Figure below shows an output of the applied methodology. Here, the travel times (walking, transit, and the combination of transit and walking) to the closest grocery store by time of day are shown for 4am, 8am, 1pm, and 10pm. The variable N represents the number of open grocery stores for each time of day.",
                  image: {
                    src: require("@/assets/images/PIT/Transit_acc_1.jpg"),
                    alt: "Urban Accessibility Index Map",
                    caption:
                      "Spatial Based Travel Times to Open Grocery Stores by Time of Day",
                  },
                },
                {
                  id: "tc",
                  name: "Transit Connectivity",
                  description: "",
                  definition:
                    "Transferring between transit modes is a critical component of urban transit services. Considerations such as on-time arrivals, time to walk between transit services, and the number of transfers required to get from a given origin to a given destination impact transit connectivity.",
                  challenges: {
                    description:
                      "Estimating transit connectivity requires data to be collected on critical OD pairs in the service area. This data is often collected from in-vehicle GPS systems, anonymized cell phone data, or from household transportation surveys.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Transit routes and schedule",
                    "Time to walk to transfer location",
                  ],
                  enhancementOpportunities: ["Transit ridership"],
                  useCase:
                    "The paper by Liu et al. (2022) developed methods to measure and enhance connectivity in a rail transit network. The study defined a connectivity reliability metric that measures variation in travel times between OD pairs on a transit network. The method was applied to a real-world network in Chengdu, China. Figure below presents the output of the connectivity reliability metric in Chengdu. Here, higher connectivity scores are collared yellow, orange, and green, while lower connectivity scores were represented by shades of blue.",
                  image: {
                    src: require("@/assets/images/PIT/Transit_acc_2.jpg"),
                    alt: "Transit Connectivity Map",
                    caption:
                      "Contours of Connectivity Reliability During Morning Peak Period",
                  },
                },
                {
                  id: "percentage-of-population",
                  name:
                    "Percentage of Population with Access to Public Transit",
                  description: "",
                  definition:
                    "Another performance indicator for transit accessibility is the estimation of the percent of the population with access to transit. There are several ways to dissect the population as well, such as by race and income level. These details enable enhanced analysis such as equity of transit services.",
                  challenges: {
                    description:
                      "Estimating the percentage of the population with access to public transit requires knowledge of the local population and estimates on the distance or travel time to access transit. When available, sociodemographic data enables the analysis of specific demographics within the total population. Knowledge on the transit system stops, stations, and schedules are needed to assess accessibility of transit OD pairs.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Estimate of urban populations - Often derived from transportation surveys or open-source population data such as WorldPop.",
                    "Travel times and distances - An estimate of the time to travel to opportunities and critical services using public transportation, including walking.",
                  ],
                  enhancementOpportunities: [
                    "Socio-demographic/census data - Sociodemographic data on population distributions, income, and employment. This data is often collected and managed by local or national agencies.",
                  ],
                  useCase:
                    "The research by Ermagun and Tilahun (2020) evaluated the equity of transit accessibility in Chicago, Illinois (USA). The study employed a travel time to access various opportunities and essential services by transit in 30 minutes. The study found that while transit accessibility generally increased with the percentage of car-less households, it declines with increases in the percentage of low-income households. The study also found that larger percentages of African American people decrease transit access to jobs and groceries.",
                  image: null,
                },
              ],
            },
            {
              id: "mob",
              name: "Mobility of People",
              icon: "fas fa-users",
              description: "",
              indicators: [
                {
                  id: "tr",
                  name: "Transit Ridership",
                  description: "",
                  definition:
                    "A cornerstone data item for transit is the estimation of ridership. Ridership can be used to estimate revenues, delay costs, system-level demand, and demand between OD pairs.",
                  challenges: {
                    description:
                      "Estimating ridership is influenced by the fare collection system:",
                    bullets: [
                      "Cash-based transit services provide information on general ridership (via fares collected) but lack details such as demand between OD pairs.",
                      "Fare card systems with swipe-in/swipe-out functionality provide details on individual trip origins and destinations.",
                    ],
                  },
                  dataRequirements: [
                    "Transit Route and Schedule",
                    "Transit ridership \u2013 Information about person hours of delay on transit.",
                  ],
                  enhancementOpportunities: [
                    "Service alerts and trip modifications \u2013 Information on changes to scheduled services such as delays due to emergency track/road work and modified routes impact urban accessibility.",
                  ],
                  useCase:
                    "The study by de Ingenier\u00eda et al. (2020) developed a method to estimate transit ridership when little or no data exists. The method was applied to the transit system in the city of Mexicali, Mexico. The study used other known parameters, such as bus stop coverage and headways, differences in travel times between cars and transit, and population density to model transit ridership. The study found that bus stop coverage was not related to ridership. However, the travel times and perception of the transit services did impact ridership.",
                  image: null,
                },
                {
                  id: "transit-travel-times",
                  name: "Transit Travel Times and Delay",
                  description: "",
                  definition:
                    "Transit travel times and delay offer insights on the on-time performance of transit services, such as light rail and buses. These metrics also support the assessment of the efficiency of transit transfers (e.g. bus to light rail).",
                  challenges: {
                    description: "",
                    bullets: [
                      "Collecting system-level travel times requires installing and maintaining GPS devices on each transit vehicle.",
                      "Assessing the efficiency of transit transfers needs detailed, time-stamped vehicle location data and passenger transfer times.",
                      "Equip transit vehicles with GPS to monitor schedule adherence.",
                      "Collect and share detailed transit data with the GTFS community to improve efficiency assessments.",
                    ],
                  },
                  dataRequirements: [
                    "Transit Route and Schedule",
                    "Transit travel times and distances",
                  ],
                  enhancementOpportunities: [
                    "Transit ridership",
                    "Service alerts and trip modifications",
                  ],
                  useCase:
                    "The 2020 work by Liao et al. compared personal vehicle driving times to transit travel times in S\u00e3o Paulo, Brazil; Stockholm, Sweden; Sydney, Australia; and Amsterdam, Netherlands. The study found that on average, transit travel times were 1.4 - 2.6 times longer than driving a car.",
                  image: null,
                },
                {
                  id: "tbi",
                  name: "Transit Bus Infrastructure",
                  description: "",
                  definition:
                    "Transit bus infrastructure assessments require the evaluation of buses, route and road surface conditions, bus stops, and bus storage facilities.",
                  challenges: {
                    description: "",
                    bullets: [
                      "Collecting data on bus transit infrastructure requires coordination with roadway agencies, which maintain road conditions.",
                      "Transit agencies must also assess the conditions of other assets, such as bus stops and storage facilities.",
                      "Regularly inspect buses to ensure safety.",
                      "Monitor the condition of bus stops and storage facilities.",
                      "Implement mechanisms for users to report infrastructure concerns.",
                      "Store and manage this data in transit agency databases.",
                    ],
                  },
                  dataRequirements: [
                    "Conditions of bus transit assets - Regular evaluation of buses, bus stops, road condition on routes, and bus storage facilities are required for infrastructure management. This data is collected and maintained by transit agencies.",
                  ],
                  enhancementOpportunities: [
                    "Planned roadwork - Coordinating with roadway departments allows for knowledge of maintenance activities that impact all road users. This data is often found in construction permitting databases for roadway projects.",
                  ],
                  useCase:
                    "The publication by the American Society of Civil Engineers (ASCE) - Infrastructure Report Card Team summarized the challenges in maintaining the condition of transit systems. The report found that increasing procurement and maintenance costs, combined with decreased ridership since the COVID-19 pandemic, present significant challenges for United States transit agencies. In addition, the 2024 Status of the Nation\u2019s Highways, Bridges, and Transit - Conditions and Performance by the USDOT found that 36.4 percent of facilities (bus and rail maintenance buildings and equipment storage yards) were rated in poor condition. It also found that 18.5 percent of transit vehicles (bus and rail) were rated in poor condition.",
                  image: null,
                },
              ],
            },
            {
              id: "saf",
              name: "Safety",
              icon: "fas fa-shield-alt",
              description: "",
              indicators: [
                {
                  id: "transit-crashes-and",
                  name: "Transit Crashes and Crash Rates",
                  description: "",
                  definition:
                    "While transit vehicle VMT may be used to estimate crash rates, passenger miles traveled (PMT) account for the riders on the transit vehicle.",
                  challenges: {
                    description: "",
                    bullets: [
                      "Gathering information on transit injuries, especially for non-transit riders such as motorists and pedestrians.",
                      "Information about the thoroughness of the crash investigator in documenting the crash scene and contributing factors.",
                      "Foster collaborative relationships and trust across modal agencies to enable data sharing.",
                    ],
                  },
                  dataRequirements: [
                    "Date, time, and location of crashes",
                    "Transit VMT or PMT",
                  ],
                  enhancementOpportunities: [
                    "Severity of injuries",
                    "Number and type of vehicles involved",
                    "Details on victims",
                    "Details on the crash environment",
                    "Details on the emergency response",
                    "PTC data",
                  ],
                  useCase:
                    "The research by Nguyen et al. (2021) investigated various factors contributing to 529 transit bus crashes in Hanoi, Vietnam. The study assessed the impact of variables such as traffic density, weather, and bus size on crash severity risk. The study found that large buses, rain conditions, evening or night, sparse traffic, non-urban areas, roads with at least three lanes, curved roads, two-way roads without a physical barrier, head-on collisions, and pedestrian-related crashes all increased the severity risk of transit buses in Hanoi.",
                  image: null,
                },
                {
                  id: "transit-rider-safety",
                  name: "Transit rider safety and security",
                  description: "",
                  definition:
                    "Transit safety must also include the safety and security of public transit system users. In addition to feeling safe on public transit vehicles, safety and security must also be considered at bus stop locations, train stations, and transit hubs.",
                  challenges: {
                    description: "",
                    bullets: [
                      "Documentation of events that cause safety concerns, such as incidents of harassment, assault, theft/robbery, etc. This data may be collected by transit security employees, station managers, or by vehicle operators.",
                      "System user surveys can be designed to collect information on personal experiences and perceptions of public transit system user safety and security.",
                    ],
                  },
                  dataRequirements: [
                    "User safety/security events - Records of transit system user complaints of safety and security concerns. The records must include the date, time, location of the event, and a description of the event.",
                  ],
                  enhancementOpportunities: [
                    "Survey of transit system users - Surveys provide valuable information from real-world system users. Questions geared toward perceptions of safety, personal experiences, and general observations on transit system safety security.",
                  ],
                  useCase:
                    "The analysis by Ceccato and Loukaitou-Sideris (2022) surveyed 13,323 college student transit system users in 18 different cities across the globe. The objective of the study was to investigate how fears of sexual harassment affect behavior and mobility. The study found that student mobility was impacted by avoidance of transit system services during certain times of the day, on particular routes deemed risky, or by entirely avoiding transit.",
                  image: {
                    src: require("@/assets/images/PIT/Transit_saf_1.jpg"),
                    alt: "Transit Safety Survey Image",
                    caption: "Example of a Transit Safety Survey Question",
                  },
                },
              ],
            },
            {
              id: "sus",
              name: "Sustainability",
              icon: "fas fa-leaf",
              description: "",
              indicators: [
                {
                  id: "transit-energy-use",
                  name: "Transit energy use and emissions",
                  description: "",
                  definition:
                    "Transit has tremendous potential in reducing energy consumption and emissions from the transportation sector. Therefore, it is critical for transit agencies to collect data on the impact of transit on the environment and air quality.",
                  challenges: {
                    description: "",
                    bullets: [
                      "Collecting field data on transit vehicle emissions requires the installation of field sensors to measure emissions such as carbon dioxide (CO\u2082).",
                      "Recognizing that field measurements are beyond the resources of most transit agencies, modeling of transit energy use and emissions are often employed.",
                    ],
                  },
                  dataRequirements: [
                    "Transit routes and schedules",
                    "Transit ridership",
                  ],
                  enhancementOpportunities: [
                    "Service alerts and trip modifications",
                    "PTC data",
                  ],
                  useCase:
                    "The 2022 paper by Ashik et al. examined the impact of transit-oriented developments (TOD) on CO\u2082 in Dhaka, Bangladesh. The study fused road network data from OSM, land use data from the local development authority, and the results of a household transportation survey to model CO\u2082 emissions from three types of trips for each of the 31,101 survey respondents. These trip types included home-based work, home-based school, and home-based other non-work trips. The study found that TODs have the potential to reduce CO\u2082 emissions for work and school trips, but not for other non-work trips.",
                  image: null,
                },
              ],
            },
          ],
        },
        {
          id: "nonmotor",
          name: "Non-Motorized",
          icon: "fas fa-walking",
          description: "",
          categories: [
            {
              id: "acc",
              name: "Access and Connectivity",
              icon: "fas fa-road",
              description: "",
              indicators: [
                {
                  id: "rural-access-indicator",
                  name: "Rural Access Indicator (RAI)",
                  description: "",
                  definition:
                    "The share of the rural population living within 2 kilometers of an all-season road in good or fair condition.",
                  challenges: {
                    description: "",
                    bullets: [
                      "Significant differences in data availability and quality within and across developing countries.",
                      "Using a robust and flexible RAI estimation methodology that accommodates varying data inputs, temporal and spatial resolutions, and data update frequencies.",
                    ],
                  },
                  dataRequirements: [
                    "Estimate of rural populations",
                    "Road network and road surface type/conditions",
                    "Available modes for accessing an all-season road",
                  ],
                  enhancementOpportunities: [
                    "Socio-demographic/census data",
                    "Historical weather conditions",
                    "Terrain information",
                  ],
                  useCase:
                    "The 2019 study by Workman and McPherson at the Research for Community Access Partnership offered a modernized approach to estimate RAI. The study applied their methodology to develop a global RAI measurement tool that was used to compare RAI between the 2006 World Bank study, 2016 World Bank Study, and the results of the modernized methodology.",
                  image: {
                    src: require("@/assets/images/PIT/Nonmotor_acc_1.jpg"),
                    alt: "Rural Access Indicator Map",
                    caption: "RAI Estimates by Country and Methodology",
                  },
                },
                {
                  id: "urban-accessibility-index",
                  name: "Urban Accessibility Index (UAI)",
                  description: "",
                  definition:
                    "UAI measures the distance or time to access opportunities or critical services using public transportation and walking. The World Bank's version defines it as the percentage of jobs accessible within a 60-minute commute by walking and public transportation.",
                  challenges: {
                    description: "",
                    bullets: [
                      "Estimating UAI requires fusion of multiple data sources: Population estimates, location and operating hours, transit options available to access opportunities, and critical services.",
                      "Select the most appropriate version of UAI based on data availability.",
                    ],
                  },
                  dataRequirements: [
                    "Estimate of urban populations",
                    "Points of Interest",
                    "Travel times and distances",
                  ],
                  enhancementOpportunities: [
                    "Socio-demographic/census data",
                    "Observed transit travel times",
                    "Service alerts and trip modifications",
                  ],
                  useCase:
                    "The research by J\u00e4rv et al. (2018) developed a dynamic urban accessibility model that considered the temporal variations in population, transport services, and hours of operation for points of interest. The proposed method was applied to a real-world use case in Tallinn, Estonia. Figure below shows an output of the applied methodology. Here, the travel times (walking, transit, and the combination of transit and walking) to the closest grocery store by time of day are shown for 4am, 8am, 1pm, and 10pm. The variable N represents the number of open grocery stores for each time of day.",
                  image: {
                    src: require("@/assets/images/PIT/Nonmotor_acc_2.jpg"),
                    alt: "Urban Accessibility Index Map",
                    caption:
                      "Spatial Based Travel Times to Open Grocery Stores by Time of Day",
                  },
                },
                {
                  id: "puc",
                  name: "Pedestrian Urban Connectivity",
                  description: "",
                  definition:
                    "There are many variations of pedestrian connectivity measures, such as the number of intersections per unit area, link-node ratio, and walking distance sheds (i.e., pedsheds), and pedestrian route directness (PRD).",
                  challenges: {
                    description:
                      "Care should be taken in selecting the pedestrian urban connectivity performance indicator to ensure the selected indicator meets agency needs. Perhaps the biggest challenge in estimating pedestrian urban connectivity is identifying the location and condition of sidewalks and crosswalks.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Map of road and sidewalk network - the locations of roads and availability of sidewalks. When possible, information on the road and sidewalk grade, and OSM should be considered",
                  ],
                  enhancementOpportunities: [
                    "Sidewalk conditions - Though challenging to assess at scale, information on the conditions of sidewalks can enhance connectivity indicators.",
                  ],
                  useCase:
                    "The work by Pearce et al. (2021) compared several pedestrian network connectivity metrics in the Asian cities of Bangkok, Thailand, Manila, Philippines, Osaka, Japan, and Taipei, Taiwan. The study used several open source map services (OSM, Google Earth, and Google Maps) to map the pedestrian networks within 400m of selected metro stations in each city. Figure below presents the percentage of various components in each network.",
                  image: {
                    src: require("@/assets/images/PIT/Nonmotor_acc_3.jpg"),
                    alt: "Pedestrian Network Components Chart",
                    caption: "Composition of Pedestrian Paths by City",
                  },
                },
                {
                  id: "dpa",
                  name: "Disabled Pedestrian Accessibility",
                  description: "",
                  definition:
                    "It is critical to assess the quality of the pedestrian network in enabling the efficient mobility of disabled people. Such assessments must consider variables such as grades of sidewalks and ramps, coverage of drains, and presence of obstacles such as signs or utility poles.",
                  challenges: {
                    description: "",
                    bullets: [
                      "Collecting field network-level data on sidewalk and ramp conditions can be a daunting task.",
                      "Methods based on satellite imagery or household transportation surveys are often employed to identify portions of the pedestrian network that may present challenges for people with disabilities.",
                    ],
                  },
                  dataRequirements: [
                    "Map of road and sidewalk network",
                    "Sidewalk conditions",
                  ],
                  enhancementOpportunities: [
                    "Location of traffic control devices - Knowledge of traffic signs and other assets, such as the height of pedestrian signal call buttons, can enhance the evaluation of pedestrian infrastructure for people with disabilities.",
                  ],
                  useCase:
                    "The report by Danso et al. (2023) documented the effort to assess the accessibility of the pedestrian infrastructure to people with disabilities in Kumasi, Ghana. The study interviewed 112 persons with disabilities (PWD) who use the pedestrian network. The study found that almost 90 percent of interviewed PWDs said movement on the pedestrian network was not easy due to challenges in locating crossings and not feeling safe at crossings.",
                  image: {
                    src: require("@/assets/images/PIT/Nonmotor_acc_4.jpg"),
                    alt: "Disabled Pedestrian Accessibility Image",
                    caption:
                      "Proportions of Routes on Bicycle Facilities and Diversions from Shortest Paths for OD Pairs in Montreal",
                  },
                },
                {
                  id: "bc",
                  name: "Bicycle Connectivity",
                  description: "",
                  definition:
                    "Similar to pedestrian connectivity, bicycle connectivity measures directness of bicycle facilities between OD pairs. Bicycle connectivity is calculated as the percentage of the trip length on bicycle facilities and the percentage of trip length on diversions from the shortest path for a given OD pair.",
                  challenges: {
                    description:
                      "Estimation requires knowledge of common bicycle OD pairs and the shortest path to travel on the bicycle network. Common OD pairs are often gathered from household transportation surveys, while details of the bicycle network may be found in an agency's asset management database or from open data sources such as OSM.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Map of bicycle network - The locations of roads and paths accessible to bicyclists are required for establishing the bicycle network and shortest paths between OD pairs.",
                    "Bicycle OD pairs - Field collection of bicycle OD pairs is difficult due to the challenges in tracking individual bicycle trips.",
                  ],
                  enhancementOpportunities: [
                    "Bicycle facility conditions - Information on bicycle path grades and level of stress on portions of the path that are shared with motor vehicle traffic.",
                  ],
                  useCase:
                    "The research by Boisjoly et al. (2020) developed and applied bicycle connectivity performance indicators for the bicycle network in Montreal, Canada. The study leveraged a household transportation survey to determine common bicycle OD pairs and routes. The shortest paths were compared to routes reported in the household transportation survey to determine the portion of the bicycle trip on the bicycle network and the proportion of the trip length on diversions from the shortest path.",
                  image: null,
                },
              ],
            },
            {
              id: "saf",
              name: "Safety",
              icon: "fas fa-shield-alt",
              description: "",
              indicators: [
                {
                  id: "pc",
                  name: "Pedestrian Crashes",
                  description: "",
                  definition:
                    "A cornerstone in understanding pedestrian safety are detailed records of pedestrian-related crashes. Pedestrian crash reports provide valuable details on the crash environment such as the time and location of the crash (crosswalk or otherwise), lighting conditions, status of traffic control devices (i.e. pedestrian crossing signal).",
                  challenges: {
                    description:
                      "Unlike other modes, normalization of pedestrian data can be challenging because widespread pedestrian counts require significant resources. Therefore, many studies employ statistical methods to identify and quantify the variables that affect pedestrian crash risk.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Date, time, and location of crashes - In addition, the location information should describe the pedestrian environment, such as crosswalks, sidewalks, and any pedestrian traffic control device.",
                  ],
                  enhancementOpportunities: [
                    "Pedestrian crossing volumes",
                    "Severity of injuries",
                    "Number and type of vehicles involved",
                    "Details on victims",
                    "Details on the crash environment",
                    "Details on the emergency response effort",
                  ],
                  useCase:
                    "The study by Kraidi and Evdorides (2020) analyzed pedestrian crashes in Birmingham, England with the objective of identifying variables that increase pedestrian crash risk. The study collected field data at targeted pedestrian safety hotspots. Data items collected included traffic volumes, traffic speed and speed variation, presence of roadside parking, and the number of nearby bus stops. The researchers found that the number of bus stops, parking, pedestrian crossing and violations\u2019 volume, the traffic speed variation, the number of intersecting side roads, intersecting traffic volume, were all significant variables in estimating pedestrian crash risk.",
                  image: null,
                },
                {
                  id: "bc",
                  name: "Bicycle Crashes",
                  description: "",
                  definition:
                    "Similar to pedestrian crashes, bicycle crashes reports are critical for understanding and improving safety. Bicycle crash reports should provide details on the bicyclist(s) and the environment in which the crash occurred.",
                  challenges: {
                    description:
                      "Unlike other modes, normalization of pedestrian data can be challenging as widespread pedestrian counts require significant resources. Therefore, many studies employ statistical methods to identify and quantify the variables that affect pedestrian crash risk.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Date, time, and location of crashes - In addition, the location information should describe the bicycling environment, such as the presence of bike lanes, lighting conditions, traffic volumes, and traffic speeds.",
                  ],
                  enhancementOpportunities: [
                    "Bicycle crossing volumes",
                    "Severity of injuries",
                    "Number and type of vehicles involved",
                    "Details on victims",
                    "Details on the crash environment",
                    "Details on the emergency response effort",
                  ],
                  useCase:
                    "The 2021 paper by Cai et al. investigated the factors contributing to bicycle crashes at intersections in Orlando, Florida, United States. Bicycle volume counts were collected manually at critical intersections over a three-year period. The study developed a model to estimate the occurrence of intersection bicycle crashes. The researchers found that motor vehicle volumes, bike volumes, location of bike lanes, number of road lanes, land use, and socio-demographics were found to be statistically significant in estimating the occurrence of a bicycle crash at intersections.",
                  image: null,
                },
              ],
            },
            {
              id: "sus",
              name: "Sustainability",
              icon: "fas fa-leaf",
              description: "",
              indicators: [
                {
                  id: "pedestrian-and-bicycle",
                  name: "Pedestrian and Bicycle Emissions Savings",
                  description: "",
                  definition:
                    "Walking and bicycling are both considered green transport modes as they do not contribute to fossil fuel consumption or air quality concerns.",
                  challenges: {
                    description:
                      "The reduction in carbon dioxide (CO\u2082) and greenhouse gas (GHG) resulting from trips shifting to pedestrian or bicycle trips is difficult to collect in the field; therefore, household transportation surveys are used to collect data. This data is often modeled to estimate the impact of increased walking and bicycle trips.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Mode shift to walking and bicycling - Often collected via household transportation surveys. Shifts from motor vehicle trips have the largest impact.",
                  ],
                  enhancementOpportunities: ["N/A"],
                  useCase:
                    "The work by Kou et al. (2020) developed a model to estimate the reduction in GHG from bike-share programs in eight United States cities, including New York, Chicago, Boston, Philadelphia, Washington D.C., Los Angeles, San Francisco, and Seattle. The study found that while all analyzed bike-share programs reduce GHG emissions, the results vary from city to city. As shown in the figure below, New York City had the largest estimated reduction in GHG 5,417 tons (CO2 equivalent), while Seattle was estimated to have a reduction of 41 tons (CO2 equivalent) emissions.",
                  image: {
                    src: require("@/assets/images/PIT/Nonmotor_sus_1.jpg"),
                    alt: "Bike Share GHG Emissions Reduction Chart",
                    caption:
                      "Estimated Reduction in GHG (CO2 equivalent) by City",
                  },
                },
              ],
            },
          ],
        },
        {
          id: "rail",
          name: "Rail",
          icon: "fas fa-train",
          description: "",
          categories: [
            {
              id: "acc",
              name: "Access and Connectivity",
              icon: "fas fa-road",
              description: "",
              indicators: [
                {
                  id: "ra",
                  name: "Rail Accessibility",
                  description: "",
                  definition:
                    "Like other accessibility metrics, the rail connectivity performance indicator measures how efficiently origins and destinations can be accessed on the rail network.",
                  challenges: {
                    description:
                      "Estimating rail accessibility requires data collection of critical OD pairs in the service area. Technology-based data collection or household transportation surveys.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Map of rail network",
                    "Rail schedule",
                    "Demand between OD pairs",
                  ],
                  enhancementOpportunities: [
                    "Observed travel times - Data on the adherence to the schedule impacts accessibility.",
                  ],
                  useCase:
                    "The research by Martin et al. (2021) estimated the costs and benefits of developing a high-speed rail (HSR) system in Spain. The study weighed the accessibility performance indicator based on the populations of OD pairs and the travel time. The study examined the changes in connectivity at various stages of the HSR network construction, including 1990, 2007, 2016, and 2025 (future phase). Table below presents the changes in HSR connectivity between analysis years. The authors noted that the largest increases in HRS connectivity resulted from connecting cities with large populations.",
                  image: {
                    src: require("@/assets/images/PIT/Rail_acc_1.jpg"),
                    alt: "Rail Accessibility Table",
                    caption:
                      "Changes in HSR Connectivity in Spain Across Different Time Periods",
                  },
                },
              ],
            },
            {
              id: "mob",
              name: "Mobility of People",
              icon: "fas fa-users",
              description: "",
              indicators: [
                {
                  id: "rail-infrastructure-and",
                  name: "Rail Infrastructure and Asset Management",
                  description: "",
                  definition:
                    "Maintenance of rail infrastructure is required to ensure safe, efficient, and reliable rail service. These performance measures are often referred to as key performance indicators (KPI).",
                  challenges: {
                    description:
                      "Regular monitoring of railroad assets relies on both technology and expert evaluation. KPIs include the age and condition of network components, structures, rail yards, tracks, trains, and various failures. Despite the diverse assets in the rail industry, data collection methods for each performance measure are typically uniform.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Map of rail network",
                    "Documentation of system component failures",
                    "Assessment of rail asset condition",
                  ],
                  enhancementOpportunities: ["N/A"],
                  useCase:
                    "The publication by Kalem et al. (2024) developed a hybrid fuzzy multi-criteria decision model (MCDM) to evaluate railroad infrastructure management across Europe. The study considered several dimensions such as safety, system growth, and condition of the railroad infrastructure. The model included 35 KPIs and each KPI was given a weight in the MCDM model. The research found that the percentage of canceled trains, number of minutes of delay per kilometer, and serious injuries were all highly impactful on the assessment of railroad infrastructure.",
                  image: null,
                },
              ],
            },
            {
              id: "saf",
              name: "Safety",
              icon: "fas fa-shield-alt",
              description: "",
              indicators: [
                {
                  id: "rail-crashes-and",
                  name: "Rail Crashes and Crash Rates",
                  description: "",
                  definition:
                    "Railroad crash and crash rate analysis is similar to transit analysis. However, unlike transit rail, data sharing across agencies is less common in intercity passenger and freight rail.",
                  challenges: {
                    description:
                      "Detailed reporting of rail crashes is needed to understand the factors contributing to the crash. Therefore, timely crash investigations by trained rail safety experts are required.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Minimum railroad crash data requirements are similar to rail transit.",
                  ],
                  enhancementOpportunities: [
                    "Additional railroad crash data are similar to rail transit.",
                  ],
                  useCase:
                    "The 2023 analysis by Blumenfeld et al. assessed the railroad data availability in 148 countries between 2015 - 2020. Next, the authors compared each country's safety performance across the five key performance indicators (KPI). The KPIs included in the study were fatalities per million train-km (MTK), major injuries per MTK, level crossing incidents per thousand level crossings, derailments per MTK, and collisions per MTK. As shown in Figure below, the availability of each KPI varied across each country and analysis year. This finding illustrates the need for the rail industry to establish common safety metrics to support industry-level safety assessments.",
                  image: {
                    src: require("@/assets/images/PIT/Rail_saf_1.jpg"),
                    alt:
                      "Rail Safety Key Performance Indicators Availability Chart",
                    caption:
                      "Number of Countries with Data to Support the Five KPIs (note, LX is a level crossing)",
                  },
                },
              ],
            },
            {
              id: "sus",
              name: "Sustainability",
              icon: "fas fa-leaf",
              description: "",
              indicators: [
                {
                  id: "ghg-emissions-and",
                  name: "GHG Emissions and Fossil Energy Consumption",
                  description: "",
                  definition:
                    "Recognizing that intercity and freight rail often use fossil fuels as the energy source, monitoring fuel consumption and the emissions of GHG are required for rail sustainability.",
                  challenges: {
                    description:
                      "Similar to transit rail, field collection of fuel consumption and emissions can be challenging. Therefore, most agencies estimate these indicators with mathematical models.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Minimum railroad sustainability data requirements are similar to rail transit.",
                  ],
                  enhancementOpportunities: [
                    "Additional railroad sustainability data are similar to rail transit.",
                  ],
                  useCase:
                    "The work by Djordjevi\u0107 (2021) developed indices to evaluate the performance of rail in several European countries. The study used the estimation of GHG, fossil fuel consumption, and density of the rail network as key indicators of rail sustainability. The study assessed the correlation of these sustainability indicators with other factors such as rail demand, rail safety, and rail accessibility. As expected, the study reported strong correlation with fuel consumption and GHG emissions. These two sustainability metrics were also correlated with revenue, expenditures in the rail network, and passenger volume.",
                  image: null,
                },
              ],
            },
            {
              id: "fre",
              name: "Freight and Trade",
              icon: "fas fa-boxes",
              description: "",
              indicators: [
                {
                  id: "freight-rail-movement",
                  name: "Freight Rail Movement and Efficiency",
                  description: "",
                  definition:
                    "Freight rail is a cornerstone of most countries' economies. Freight rail offers a low-cost, safe, and efficient means of moving goods, especially for longer distance deliveries.",
                  challenges: {
                    description:
                      "Perhaps the biggest challenge in intercity and freight rail analysis is data availability. While most railroad agencies collect data on the movement of people and freight, few share the details with outside parties. Therefore, conducting industry-level analysis can be difficult. Some countries have overcome this barrier by establishing data trusts that break down the silos of this data while protecting the operational details of the railroads.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Map of rail network",
                    "Freight rail operational costs",
                    "Freight rail travel times",
                    "Freight rail fuel consumption",
                    "Freight rail throughput",
                  ],
                  enhancementOpportunities: ["Commodity Flows"],
                  useCase:
                    "The paper by Daramola (2022) compared freight movements by truck versus rail. The study focused on the Lagos\u2013Kano corridor in Nigeria. The study compared truck freight movements to freight rail movements using the performance indicators of freight throughput per operational cost, freight throughput per energy use, revenue per freight throughput, and freight-ton kilometer per hour traveled. The study found that rail freight movement was less expensive and more sustainable than trucks. However, average speeds and freight throughput were higher for trucks than rail. The author noted that the slow travel times on rail was a considerable deterrent.",
                  image: null,
                },
              ],
            },
          ],
        },
        {
          id: "maritime",
          name: "Maritime",
          icon: "fas fa-ship",
          description: "",
          categories: [
            {
              id: "acc",
              name: "Access and Connectivity",
              icon: "fas fa-road",
              description: "",
              indicators: [
                {
                  id: "ma",
                  name: "Maritime Accessibility",
                  description: "",
                  definition:
                    "Like other accessibility metrics, the maritime connectivity performance indicator measures how efficiently origins and destinations can be accessed by ships. Accessibility is impacted by port infrastructure and capacity.",
                  challenges: {
                    description:
                      "Assessing maritime connectivity requires data collection and sharing at a global scale. While each port and shipping company collects data on freight movements, accessing such data requires collaboration. Global entities such as the UN Trade and Development (UNCTAD) report on freight accessibility on an annual basis.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Global Port Locations and Capacities - The location and capacity of ports are required to assess maritime connectivity.",
                    "Demand between OD pairs - Demand between critical port OD pairs is needed.",
                  ],
                  enhancementOpportunities: [
                    "Observed travel times - Data on the freight ship travel times are needed to determine the efficiency of the route.",
                  ],
                  useCase:
                    "The 2021 report by the United Nations Trade and Development (UNCTAD) identified several maritime shipping performance indicators for ports and ships. The study assessed linear connectivity as a key metric in maritime performance. The study assessed how well global economies were connected via the linear connectivity metric. The study found that the top five connected economies were in Asia (China, Singapore, Republic of Korea, and Hong Kong, China), while the United States, Spain, the Netherlands, United Kingdom, and Belgium rounded out the top ten connected economies. The report indicated the gap between most and least connected economies was widening.",
                  image: null,
                },
              ],
            },
            {
              id: "saf",
              name: "Safety",
              icon: "fas fa-shield-alt",
              description: "",
              indicators: [
                {
                  id: "maritime-safety",
                  name: "Maritime Safety",
                  description:
                    "Frequency and severity of crashes and injuries on ships.",
                  definition:
                    "Maritime safety is generally reported as the frequency of crashes on ships and the associated injuries. This data may be normalized by the distance traveled by ships, though global data is not universally available.",
                  challenges: {
                    description:
                      "Recording all maritime crashes globally is challenging, but high-profile incidents are generally well-known. Obtaining details like crash environments and ship operator information can be difficult. Organizations like the International Maritime Organization (IMO) collect data on maritime safety, security, and sustainability.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Crash location and time – Needed to document when and where the crash occurred.",
                    "Injury details – Number of individuals injured and severity of injuries to assess total cost.",
                  ],
                  enhancementOpportunities: [
                    "Information of the ship operator – Years of experience, training, and hours worked before the crash provide valuable insights on human factors that may have contributed to the crash.",
                  ],
                  useCase:
                    "The study by Wang et al. (2021) investigated factors impacting the severity of global maritime crashes between 2010-2019, including 1,207 incidents. The study found that sinking accidents, far from port, strong wind, heavy sea, strong current, and/or good visibility were positively correlated with accident severity. Fishing vessels, yachts, and sailing vessels are more likely to be involved in higher severity incidents.",
                  image: null,
                },
              ],
            },
            {
              id: "sus",
              name: "Sustainability",
              icon: "fas fa-leaf",
              description: "",
              indicators: [
                {
                  id: "maritime-ghg-emissions",
                  name: "Maritime GHG Emissions",
                  description:
                    "Assessment of maritime fuel consumption and emissions.",
                  definition:
                    "As global demand for maritime transport increases, estimating maritime fuel consumption and emissions is critical to ensure maritime sustainability.",
                  challenges: {
                    description:
                      "Estimating maritime emissions is difficult due to the global nature of maritime movements. Most estimates come from models using inputs such as ship type, fuel type, and distance traveled.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Country of trip origin/destination",
                    "Ship type/size and age",
                    "Trade lanes",
                  ],
                  enhancementOpportunities: ["N/A"],
                  useCase:
                    "The 2020 analysis by Greene et al. investigated the well-to-tank carbon emissions from maritime transport. The study analyzed 28,000 shipments to estimate each trip’s carbon emissions using Clipper Data Ltd. and emission models based on speed, engine type, and fuel consumption. Trade lanes between the Arab Gulf-North America and Latin America-Southeast Asia had the highest rate of CO2 emissions.",
                  image: {
                    src: require("@/assets/images/PIT/Maritime_sus_1.jpg"),
                    alt: "Maritime Emissions by Trade Lane Chart",
                    caption: "CO2 Emissions by Trade Lane",
                  },
                },
              ],
            },
            {
              id: "fre",
              name: "Freight and Trade",
              icon: "fas fa-boxes",
              description: "",
              indicators: [
                {
                  id: "container-port-performance",
                  name: "Container Port Performance Index (CPPI)",
                  description: "",
                  definition:
                    "Efficiency of maritime freight movements requires minimizing the time a vessel spends at port. To enable comparisons of port performance, the World Bank developed CPPI.",
                  challenges: {
                    description:
                      "CPPI is the average port hours per port call, defined as the time elapsed from when a vessel enters port to when it departs from the berth. The ships entering and exiting a given port should be organized by ship size, often in Twenty Foot Equivalent Units (TEU) and by the number of port calls for each ship size group.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Number of sport calls aggregated by vessel size - Number of ships in each size group and the impact of ship size on time spent in port.",
                    "Time spent in port - Time spent in port is needed for each ship that uses the target port.",
                  ],
                  enhancementOpportunities: ["N/A"],
                  useCase:
                    "The 2022 report by the World Bank applied the CPPI performance indicator to rank 348 ports across the globe. The table below presents an excerpt from the World Bank report, showing the top ten performing ports based on CPPI.",
                  image: {
                    src: require("@/assets/images/PIT/Maritime_fre_1.jpg"),
                    alt: "Container Port Performance Index Table",
                    caption: "Top Performing Ports Based on CPPI",
                  },
                },
              ],
            },
          ],
        },
        {
          id: "aviation",
          name: "Aviation",
          icon: "fas fa-plane",
          description: "",
          categories: [
            {
              id: "acc",
              name: "Access and Connectivity",
              icon: "fas fa-road",
              description: "",
              indicators: [
                {
                  id: "air-connectivity-index",
                  name: "Air Connectivity Index (ACI)",
                  description: "",
                  definition:
                    "Like other accessibility metrics, the ACI performance indicator measures how efficiently origins and destinations can be accessed by planes.",
                  challenges: {
                    description:
                      "Global connectivity assessment requires knowledge of international flight schedules of both direct and indirect connections. Fortunately, there are several open source databases such as Flightaware. Another option is the SRS Analyzer System that tracks flights on a global scale.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Global aviation schedules - Both the directness and frequency of services are needed to assess the strength of connection.",
                    "Estimated cost of connection - The cost should also include the total travel time, including layovers, to travel between an aviation OD pair.",
                  ],
                  enhancementOpportunities: [
                    "Observed travel times and on-time performance - Recognizing that adherence to flight schedules impacts connectivity, observed travel times are needed to determine the efficiency of the connection, especially on a multi-leg trip.",
                  ],
                  useCase:
                    "The research by Arvis and Shepard (2016) developed an ACI that leveraged gravity-based models and travel costs. The methodology was applied to one month of data from 2007. The study found that countries where ACI increases (representing higher connectivity) are correlated with attraction of trips, and the cost to travel is relatively low.",
                  image: null,
                },
              ],
            },
            {
              id: "mob",
              name: "Mobility of People",
              icon: "fas fa-users",
              description: "",
              indicators: [
                {
                  id: "on-time-performance",
                  name: "On-time performance and passenger delay",
                  description: "",
                  definition:
                    "Aviation system user delay has impacts on the economy and passenger confidence in aviation services. Measuring on-time arrivals and delays provide insights on the quality of service provided by aviation agencies.",
                  challenges: {
                    description:
                      "On-time arrival and delay analysis requires data to be collected on the schedule and observed arrival times. Delay can be caused by several factors such as weather, air traffic control issues, and plane maintenance issues.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Aviation Schedules - The departure and arrival schedule is required to measure on-time arrivals and associated delays.",
                    "Observed departure and arrival times",
                  ],
                  enhancementOpportunities: [
                    "Historical travel times for each OD pair - Historical data on the travel time between OD pairs provides insights on variation in flight time. This variation can be used to establish a buffer time to account for inherent travel time variation for specific OD pairs.",
                  ],
                  useCase:
                    "The analysis by Fu et al. (2020) examined the impacts of on-time performance policies in China that were enacted in 2017. The study computed average delay and buffer times for Chinese airports and airline service providers. The average delay and buffer times were calculated for the winter of 2016 (before policy) and the winter of 2017 (after policy) to assess the impact of the on-time arrival policy. The researchers discovered that on-time performance and delay were generally improved for domestic flights, however the policy is inconsistent with international policies on airline service operations.",
                  image: null,
                },
                {
                  id: "airport-runway-pavement",
                  name: "Airport runway pavement condition indices",
                  description: "",
                  definition:
                    "A set of metrics used to evaluate the condition of airport runways and taxiways. These metrics include the Pavement Condition Index (PCI), Present Serviceability Index (PSI), International Roughness Index (IRI), rutting (RUT), estimated texture depth (ETD), and deviation from a 3-meter straightedge (RSE).",
                  challenges: {
                    description:
                      "Collecting data on airport runway conditions can be done with instruments/technology or from software packages that model pavement conditions. Conditions should be measured and documented on a regular basis. Most agencies have maintenance schedules to ensure the pavement conditions are evaluated on a regular basis.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Pavement Distress",
                    "Pavement roughness",
                    "Pavement evenness",
                  ],
                  enhancementOpportunities: ["N/A"],
                  useCase:
                    "The analysis by Miah et al. (2020) provided an overview of airport pavement management systems (APMS). The study provides guidance for measuring airport pavement conditions and recommendations for how frequently each performance indicator should be measured (Table below).",
                  image: {
                    src: require("@/assets/images/PIT/Aviation_mob_1.jpg"),
                    alt:
                      "Airport Pavement Condition Measurement Frequency Table",
                    caption:
                      "Recommended Frequency for Measuring Airport Pavement Condition Indicators",
                  },
                },
              ],
            },
            {
              id: "saf",
              name: "Safety",
              icon: "fas fa-shield-alt",
              description: "",
              indicators: [
                {
                  id: "aviation-crash-and",
                  name: "Aviation crash and crash rates",
                  description: "",
                  definition:
                    "Similar to other modes, documenting crashes and injuries is required to monitor the safety performance of the aviation industry. Collecting details on crashes and injuries provides valuable insights on the factors contributing to the crash or injury event.",
                  challenges: {
                    description:
                      "While each airline and airport maintains its own safety database, accessing the detailed crash and injury reports can be challenging. However, organizations such as the UN Aviation Organization gather and make this data available.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Crash and injury reports: Details on the crash and/or injury environment are required to document the date, time, location, and contributing factors.",
                    "Aviation Schedules: At a minimum, data on the number of flights is required to estimate rates.",
                  ],
                  enhancementOpportunities: [
                    "Passenger Miles Traveled: Another option for estimating aviation injury rates incorporates passenger miles traveled. This normalization factor considers the number of passengers and trip lengths.",
                  ],
                  useCase:
                    "The report by ICAO (2019) evaluated the safety performance of the global civilian aviation industry between 2014-2018. As shown in Figure A, the overall trend in the number of accidents and accident rates had a decreasing trend between 2014-2016, followed by an increasing trend between 2016-2018. The study also broke down accidents by occurrence categories. As shown in Figure B, the categories of runway safety, injuries to and/or incapacitation of persons, and ground safety were the top three categories for aviation accidents.",
                  image: [
                    {
                      src: require("@/assets/images/PIT/Aviation_saf_1.jpg"),
                      alt: "Global Aviation Safety Charts",
                      caption:
                        "Accident counts and rates for commercial operations (2014-2018)",
                    },
                    {
                      src: require("@/assets/images/PIT/Aviation_saf_2.jpg"),
                      alt: "Global Aviation Safety Charts",
                      caption: "Accident counts by category (2014-2018)",
                    },
                  ],
                },
              ],
            },
            {
              id: "sus",
              name: "Sustainability",
              icon: "fas fa-leaf",
              description: "",
              indicators: [
                {
                  id: "ae",
                  name: "Aviation emissions",
                  description: "",
                  definition:
                    "Conventional jet fuel is derived from fossil fuels and therefore produces emissions when burned. Emissions such as CO2 have a negative impact on air quality and climate change. Therefore, monitoring the emissions from the aviation sector is critical for ensuring a sustainable transportation system.",
                  challenges: {
                    description:
                      "Direct field measurements of aviation emissions require significant investment in air quality sensors. To overcome this challenge, fuel consumption models have been developed to estimate aviation emissions based on parameters such as fuel type, plane type/engine, speed, altitude, and distance traveled.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Aviation schedules: Data on the number of flights and their distances are required to estimate emissions.",
                    "Fuel and engine type: The fuel and engine type used by each plane is needed to estimate the fuel consumption and emissions rates.",
                  ],
                  enhancementOpportunities: [
                    "Flight altitude: Data on the time spent in various altitude ranges provides information about how emissions are entered and dispersed in the atmosphere.",
                  ],
                  useCase:
                    "The 2023 work by Puliafito investigated the impact of civil aviation on air quality and emissions in Argentina. The study developed and applied an aviation emission model to estimate the magnitude of various emissions such as CO2, hydrocarbons (HC), carbon monoxide (CO), nitrous oxides (NOx), and particulate matter (PM) resulting from domestic flights. A sample output visualization is provided in the figure below which illustrates the annual CO2 emissions from domestic OD pairs in Argentina.",
                  image: {
                    src: require("@/assets/images/PIT/Aviation_sus_1.jpg"),
                    alt: "Aviation Emissions Map",
                    caption:
                      "CO2 Emissions from Domestic Argentine Flights in 2019",
                  },
                },
              ],
            },
            {
              id: "fre",
              name: "Freight and Trade",
              icon: "fas fa-boxes",
              description: "",
              indicators: [
                {
                  id: "aviation-fre-1",
                  name: "",
                  description: "",
                  definition:
                    "Though generally more expensive than truck and train freight movements, aviation provides an essential service for transporting high value and perishable goods. Therefore, monitoring the performance of aviation freight movements are critical for economic analyses.",
                  challenges: {
                    description:
                      "Monitoring aviation freight movements requires documentation of freight trip OD pairs. When possible, an estimation of the commodities and their values enables a detailed economic analysis.",
                    bullets: [],
                  },
                  dataRequirements: [
                    "Freight Aviation Schedules: Data on the number of flights and their distances are required to track freight movements. This data is maintained by air industry service providers, airports, and is available in open source databases such as Flightaware.",
                  ],
                  enhancementOpportunities: [
                    "Commodity information: Data on the time spent in various altitude ranges provides information on how emissions are entered and dispersed in the atmosphere. This data is maintained by air industry freight service providers, and airports. In some countries, a general description of the commodities may be available.",
                  ],
                  useCase:
                    "The 2022 analysis by Bartulovi\u0107 et al. (2022) investigated air freight transport in the EU and in other countries across the world. Using data from Eurostat, the researchers evaluated trends in modal freight movement between 2010-2020. As presented in Figure below, the study discovered that aviation freight movements in the European Union (EU) account for 0.1 percent of freight volumes, but represent 35 percent of freight value.",
                  image: {
                    src: require("@/assets/images/PIT/Aviation_fre_1.jpg"),
                    alt: "Aviation Freight Volume and Value Chart",
                    caption: "EU Freight Tonnage Transport by Mode",
                  },
                },
              ],
            },
          ],
        },

        {
          id:'WBGindicator',
          type: 'indicator',
          name: 'WBG results indicator',
          description: 'This indicator is used in the WBG Transport Results Framework to measure the number of people benefiting from improved transport services as a result of World Bank Group interventions. It captures the direct impact of projects on individuals who experience enhanced access, reduced travel times, and improved safety due to infrastructure and service improvements.',
          definition: 'Beneficiaries of improved transport conditions in urban and rural contexts enabled by IBRD, IDA, IFC, and MIGA operations. It assesses the number of people that experience improved access to sustainable transport infrastructure or services that have been built or rehabilitated through financed or guaranteed interventions (e.g., climate-resilient highways, rural roads, urban and interurban roads, non-motorized transport facilities, public transport, railways, ports, and airports). It will build on SDG 11.2 to systematically measure improvements in sustainable transport in countries that are financed or guaranteed through IBRD, IDA, IFC, and MIGA interventions.',
          indicators:[
            {
              title: 'Sub indicators to this main indicator:',
              bullets: [
                "Direct users that benefit from improved access to sustainable transport infrastructure and services.",
                "Inferred beneficiaries that benefit from improved access to sustainable transport infrastructure and services.",
                "Inferred beneficiaries of improvements to dedicated freight transport infrastructure and services."
              ]
            },
            {
              title: "Definition of each sub indicator:",
                bullets: [
                "Direct users: People directly benefiting from new transport services and infrastructure and/or improvements in transport services and infrastructure. These are not unique users.",
                "Inferred beneficiaries: People benefiting from improved access to new transport services and/or improvements in transport services and infrastructure based on physical proximity.",
                "Inferred beneficiaries for dedicated freight projects: People benefiting from operations that improve freight transport infrastructure, and logistics and handling services. Dedicated freight projects are defined as those that service exclusively the freight segment, such as freight ports or cargo terminals, dedicated freight rail lines, and aviation freight terminals and associated infrastructure. Freight projects that are considered reduce costs and time or improve the reliability and capacity of infrastructure and services, which in turn reduces generalized costs and increases utility for society."
              ]
            }
          ]

        }
      ],
    };
  },
  methods: {
    toggleTree() {
      this.isTreeCollapsed = !this.isTreeCollapsed;
    },
    toggleMode(modeId) {
      if (this.expandedModes.includes(modeId)) {
        this.expandedModes = this.expandedModes.filter((id) => id !== modeId);
      } else {
        this.expandedModes.push(modeId);
      }
      this.selectMode(modeId);
    },
    selectMode(modeId) {
      this.selectedMode = modeId;
      this.selectedCategory = null;
    },
    selectCategory(modeId, categoryId) {
      this.selectedMode = modeId;
      this.selectedCategory = categoryId;
    },
    getCurrentMode() {
      return (
        this.transportModes.find((mode) => mode.id === this.selectedMode) || {}
      );
    },
    getCurrentCategory() {
      const mode = this.getCurrentMode();
      return (
        mode.categories?.find((cat) => cat.id === this.selectedCategory) || {}
      );
    },
  },
};
</script>

<style scoped>
.performance-indicators {
  min-height: 100vh;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

/* Hero Section */
.pi-hero {
  background: linear-gradient(135deg, #002244 0%, #003366 100%);
  color: white;
  padding: 10px 20px;
  text-align: center;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: 2rem;
  font-weight: 300;
  margin-bottom: 20px;
}

.hero-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  line-height: 1.6;
}

/* Main Container */
.main-container {
  display: flex;
  min-height: calc(100vh - 180px);
}

/* Tree Panel */
.tree-panel {
  width: 280px;
  background: #f8f9fa;
  border-right: 1px solid #dee2e6;
  transition: all 0.3s ease;
  overflow: hidden;
  position: sticky;
  top: 55px;
  height: calc(100vh - 55px);
  z-index: 100;
}

.tree-panel.collapsed {
  width: 40px;
}

.tree-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 15px;
  background: #002244;
  color: white;
  border-bottom: 1px solid #dee2e6;
}

.tree-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.collapse-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  padding: 5px;
  border-radius: 3px;
  transition: background 0.3s ease;
}

.collapse-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.tree-content {
  height: calc(100vh - 200px);
  overflow-y: auto;
}

/* Tree Structure */
.tree-structure {
  padding: 0;
}

.tree-node {
  margin-bottom: 2px;
}

.tree-item {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
  font-size: 0.85rem;
}

.tree-item:hover {
  background: #495057;
  color: white;
}

.tree-item.active::before {
  content: "•";
  color: white;
  font-size: 1.2em;
  margin-right: 8px;
  position: relative;
  left: 0px;
}

.mode-item {
  font-weight: 600;
  font-size: 0.9rem;
  padding-left: 12px;
  text-align: left;
  margin-left: 10px;
  background: #002244;
  color: white;
  border-radius: 4px;
}

.category-item {
  padding-left: 28px;
  font-weight: 500;
  text-align: left;
  margin-left: 30px;
  background: #009fda;
  color: white;
  border-radius: 4px;
}

.category-item .expand-icon {
  display: none;
}

.expand-icon {
  width: 12px;
  margin-right: 0px;
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.mode-icon,
.category-icon,
.indicator-icon {
  width: 16px;
  margin-right: 10px;
  text-align: center;
}

.item-label {
  flex: 1;
  line-height: 1.3;
}

.tree-children {
  background: rgba(0, 0, 0, 0.02);
}

.child-node .tree-children {
  background: rgba(0, 0, 0, 0.04);
}

/* Content Panel */
.content-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
}

.breadcrumb {
  padding: 15px 25px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

.content-area {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
}

/* Mode Overview */
.mode-overview {
  max-width: 100%;
}

.mode-header {
  display: flex;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #009fda;
}

.mode-icon-large {
  font-size: 3rem;
  color: #009fda;
  margin-right: 20px;
}

.mode-header h2 {
  font-size: 2.2rem;
  color: #002244;
  margin: 0;
  font-weight: 600;
}

.mode-description {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #495057;
  margin-bottom: 30px;
}

.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.category-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: #009fda;
}

.category-icon-large {
  font-size: 2.5rem;
  color: #009fda;
  margin-bottom: 15px;
}

.category-card h3 {
  font-size: 1.3rem;
  color: #002244;
  margin-bottom: 12px;
  font-weight: 600;
}

.category-card p {
  color: #6c757d;
  line-height: 1.5;
  margin-bottom: 15px;
  font-size: 0.95rem;
}

.indicator-count {
  background: #009fda;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

/* Category Content */
.category-content {
  max-width: 100%;
}

.category-header {
  display: flex;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid #009fda;
}

.category-header h2 {
  font-size: 2rem;
  color: #002244;
  margin: 0;
  font-weight: 600;
}

.category-description {
  font-size: 1.05rem;
  line-height: 1.6;
  color: #495057;
  margin-bottom: 30px;
}

.indicators-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.indicator-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.indicator-card:hover {
  background: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.indicator-header {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  background: #009fda;
  color: white;
  margin-bottom: 0;
}

.indicator-header i {
  color: white;
  margin-right: 10px;
  font-size: 1.1rem;
}

.indicator-header h3 {
  color: white;
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.indicator-card p {
  color: #6c757d;
  line-height: 1.5;
  margin: 0 0 5px 0;
  font-size: 0.9rem;
  padding: 15px 20px 0 20px;
}

.indicator-details {
  margin-top: 0;
  border-top: none;
  padding: 0 10px 0px 20px;
}

.indicator-details .detail-section {
  margin-bottom: 20px;
  background: white;
  padding: 10px;
  border-radius: 3px;
}

.indicator-details .detail-section h4 {
  color: #002244;
  font-size: 1rem;
  margin-bottom: 5px;
  font-weight: 600;
}

.indicator-details .detail-section p {
  color: #495057;
  margin-bottom: 0;
  font-size: 0.9rem;
  line-height: 1.5;
}

.indicator-details .detail-section ul {
  margin: 10px 0 0 15px;
  color: #495057;
}

.indicator-details .detail-section li {
  margin-bottom: 5px;
  font-size: 0.9rem;
}

/* Indicator Detail */
.indicator-detail {
  max-width: 900px;
}

.indicator-detail .indicator-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #009fda;
}

.indicator-detail .indicator-header h2 {
  font-size: 1.8rem;
  color: #002244;
  margin: 0;
  font-weight: 600;
}

.detail-content {
  line-height: 1.6;
}

.detail-section {
  margin-bottom: 30px;
  background: #f8f9fa;
  padding: 25px;
  border-radius: 8px;
  border-left: 4px solid #009fda;
}

.detail-section h3 {
  color: #002244;
  font-size: 1.2rem;
  margin-bottom: 15px;
  font-weight: 600;
}

.detail-section p {
  color: #495057;
  margin-bottom: 0;
  line-height: 1.6;
}

.detail-section ul {
  margin: 15px 0 0 20px;
  color: #495057;
}

.detail-section li {
  margin-bottom: 8px;
  line-height: 1.5;
}

.image-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  text-align: center;
  margin-top: 20px;
}

.image-container img {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.image-caption {
  margin-top: 15px;
  font-style: italic;
  color: #6c757d;
  font-size: 0.9rem;
}

/* Default Content */
.default-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60%;
  text-align: center;
}

.welcome-message {
  max-width: 500px;
}

.welcome-message i {
  font-size: 4rem;
  color: #009fda;
  margin-bottom: 20px;
}

.welcome-message h2 {
  color: #002244;
  margin-bottom: 15px;
  font-weight: 600;
}

.welcome-message p {
  color: #6c757d;
  font-size: 1.1rem;
  line-height: 1.6;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .main-container {
    flex-direction: column;
  }

  .tree-panel {
    width: 100%;
    max-height: 300px;
  }

  .tree-panel.collapsed {
    width: 100%;
    max-height: 60px;
  }

  .content-panel {
    min-height: calc(100vh - 480px);
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .pi-hero {
    padding: 30px 15px;
  }

  .content-area {
    padding: 20px;
  }

  .categories-grid,
  .indicators-list {
    grid-template-columns: 1fr;
  }

  .mode-header,
  .category-header {
    flex-direction: column;
    text-align: center;
  }

  .mode-icon-large,
  .category-icon-large {
    margin-right: 0;
    margin-bottom: 10px;
  }
}

@media (max-width: 640px) {
  .tree-item {
    font-size: 0.85rem;
    padding: 8px 12px;
  }

  .mode-item {
    font-size: 0.9rem;
  }

  .category-item {
    padding-left: 25px;
  }

  .indicator-item {
    padding-left: 40px;
  }

  .detail-section {
    padding: 20px;
  }
}
</style>
